import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  EmployeeDto,
  OnboardingType,
  OpenEnrollmentPeriodDto,
} from '@zorro/clients';
import { UNEXPECTED_ERROR_MESSAGE } from '@zorro/common-utils';
import { formatDateISO } from '@zorro/shared/formatters';
import {
  showErrorNotification,
  useBatchCallEndpoint,
  useForm,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect } from 'react';

import { useCreateQleBenefitDocuments } from '../../QualifyingLifeEvent';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';
import { ChangeEnrollmentForm } from './ChangeEnrollmentForm';
import {
  ChangeEnrollmentsFormFields,
  FullChangeEnrollmentSchema,
  getCurrentlyEffectiveYear,
  getLatestPlanYear,
  getSuggestedElectionWindow,
  getSuggestedExpectedStartOfCoverage,
} from './changeEnrollment.utils';

export interface AddEnrollmentRecordFormProps {
  employerId: EmployeeDto['employerId'];
  recordsForActions: RecordForActions[];
  onSuccess?: (onboardingPeriodId?: string) => void;
}

export const AddEnrollmentRecordForm = ({
  employerId,
  recordsForActions,
  onSuccess,
}: AddEnrollmentRecordFormProps) => {
  const { startLoading, stopLoading } = useLoadingOverlay();
  const queryClient = useQueryClient();

  const isSingleEmployee = recordsForActions.length === 1;

  const { data: allOpenEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const { data: associatedOnboardingPeriodsSingleEmployee = [] } =
    useMonolithQuery({
      method: 'onboardingPeriodsControllerFindMany',
      params: isSingleEmployee && [recordsForActions[0].employeeId],
    });

  const { data: singleEmployeeDto } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: isSingleEmployee && [recordsForActions[0].employeeId],
  });

  const {
    executeBatchCall: createEnrollments,
    errors: createEnrollmentsErrors,
  } = useBatchCallEndpoint({
    singularItemName: 'enrollment record',
    action: 'created',
    methodName: 'onboardingPeriodsControllerCreate',
  });

  const defaultPlanYear: OpenEnrollmentPeriodDto | undefined =
    getCurrentlyEffectiveYear(allOpenEnrollmentPeriods) ||
    getLatestPlanYear(allOpenEnrollmentPeriods) ||
    allOpenEnrollmentPeriods[0];

  const electionWindow = defaultPlanYear
    ? getSuggestedElectionWindow(
        defaultPlanYear,
        OnboardingType.SPECIAL,
        employer?.monthlySEPDeadline ?? null,
        employer?.isMonthlySEPDeadlineEndOfMonth ?? null
      )
    : undefined;
  const { data: employerAgencies } = useMonolithQuery({
    method: 'agenciesControllerFindMany',
    params: [employerId],
  });

  const enrollmentTeams = employerAgencies
    ? employerAgencies.enrollmentTeams
    : [];

  const assignModeSingleTeamDefaultValues =
    enrollmentTeams.length === 1
      ? {
          enrollmentTeamId: enrollmentTeams[0].id,
          enrollmentAgentId: undefined,
        }
      : undefined;

  const form = useForm<ChangeEnrollmentsFormFields>({
    mode: 'all',
    resolver: yupResolver(FullChangeEnrollmentSchema),
    defaultValues: defaultPlanYear
      ? {
          planYear: defaultPlanYear?.id,
          enrollmentType: OnboardingType.SPECIAL,
          isQleEnrollment: false,
          electionWindow,
          expectedStartOfCoverage: getSuggestedExpectedStartOfCoverage(
            defaultPlanYear,
            OnboardingType.SPECIAL,
            getSuggestedElectionWindow(
              defaultPlanYear,
              OnboardingType.SPECIAL,
              employer?.monthlySEPDeadline ?? null,
              employer?.isMonthlySEPDeadlineEndOfMonth ?? null
            ),
            singleEmployeeDto
          ),
          enrollmentTeamId: assignModeSingleTeamDefaultValues?.enrollmentTeamId,
        }
      : undefined,
  });

  useEffect(() => {
    void form.trigger();
  }, []);

  const { tryMutate: createOnboardingPeriod } = useMonolithMutation({
    method: 'onboardingPeriodsControllerCreate',
    successMessage: 'Enrollment record created successfully!',
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });
  const { createQleBenefitDocuments } = useCreateQleBenefitDocuments();

  const submitAddEnrollment = async (data: ChangeEnrollmentsFormFields) => {
    const {
      planYear: enrollmentPeriodId,
      enrollmentType,
      isQleEnrollment,
      qle,
      electionWindow,
      expectedStartOfCoverage,
      enrollmentTeamId,
    } = data;

    const [electionFrom, electionUntil] = electionWindow || [];

    if (
      !electionFrom ||
      !electionUntil ||
      !expectedStartOfCoverage ||
      (isQleEnrollment && !qle)
    ) {
      showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
      return;
    }

    const enrollmentData = {
      enrollmentPeriodId,
      isSpecialEnrollment: enrollmentType === OnboardingType.SPECIAL,
      onboardingFrom: formatDateISO(electionFrom),
      onboardingUntil: formatDateISO(electionUntil),
      expectedStartOfCoverage: formatDateISO(expectedStartOfCoverage),
      enrollmentTeamId,
    };

    const handleQleEnrollment = async () => {
      if (!singleEmployeeDto || !qle?.type || !qle?.occurredOn) {
        showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
        return;
      }

      const createOnboardingPeriodResult = await createOnboardingPeriod([
        {
          qualifyingLifeEvent: {
            type: qle.type,
            occurredOn: formatDateISO(qle.occurredOn),
            otherReason: qle.otherReason,
          },
          employeeId: singleEmployeeDto.id,
          employerId: singleEmployeeDto.employerId,
          ...enrollmentData,
        },
      ]);

      if (!createOnboardingPeriodResult.isOk()) return;

      const specialOnboardingPeriod = createOnboardingPeriodResult.value;

      if (qle.evidence) {
        await createQleBenefitDocuments(
          singleEmployeeDto.id,
          singleEmployeeDto.employerId,
          specialOnboardingPeriod.id,
          qle.evidence
        );
      }

      onSuccess?.(specialOnboardingPeriod.id);
    };

    const handleRegularEnrollment = async () => {
      const result = await createEnrollments(
        recordsForActions.map(({ employeeId, fullName, employerId }) => ({
          params: [{ employeeId, employerId, ...enrollmentData }],
          key: fullName,
        })),
        { shouldAvoidPageReload: true }
      );

      if (result.errors.length) return;

      if (isSingleEmployee) {
        const enrollmentResult = result.data[0];
        if (enrollmentResult?.status === 'fulfilled') {
          onSuccess?.(enrollmentResult.value.id);
          return;
        }
      }

      onSuccess?.();
    };

    startLoading();
    try {
      if (isQleEnrollment) {
        await handleQleEnrollment();
      } else {
        await handleRegularEnrollment();
      }
      await queryClient.invalidateQueries();
    } catch {
      showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
    } finally {
      stopLoading();
    }
  };
  return (
    <>
      <ChangeEnrollmentForm
        monthlySEPDeadline={employer?.monthlySEPDeadline ?? null}
        isMonthlySEPDeadlineEndOfMonth={
          employer?.isMonthlySEPDeadlineEndOfMonth ?? null
        }
        form={form}
        onSubmit={submitAddEnrollment}
        allOpenEnrollmentPeriods={allOpenEnrollmentPeriods}
        singleEmployeeDto={singleEmployeeDto}
        associatedOtherOnboardingPeriodsSingleEmployee={
          associatedOnboardingPeriodsSingleEmployee
        }
        enrollmentTeams={enrollmentTeams}
      />

      <ErrorsTable
        tableTitle="Employee name"
        errors={createEnrollmentsErrors}
        isBulk={recordsForActions.length > 1}
      />
    </>
  );
};
